import React from 'react';
import { useTranslation } from 'react-i18next';
import BrandIcon from '../brand-icon/BrandIcon';
import MatrixHeatmapTable, {
  HeatmapColumn,
  HeatmapRow,
} from '../matrix-heatmap/MatrixHeatmapTable';
import { brandColorOf } from './brandColors';
import { brandLabel } from './brandLabel';
import './ReportMatrixTable.scss';

export interface ReportMatrixRow {
  brand: string;
  isTracked: boolean;
  values: Array<number | null>;
}

interface ReportMatrixTableProps {
  /** Nagłówki kolumn w kolejności zgodnej z `values` każdego wiersza. */
  columns: string[];
  /** Podpis kolumny — modele mają własne nazwy, tematy idą wprost. */
  columnLabel?: (column: string) => string;
  rows: ReportMatrixRow[];
  /** Wspólna dla całego raportu mapa kolorów marek. */
  brandColors: Map<string, string>;
  scrollable?: boolean;
}

/**
 * Heatmapa marka × kolumna — wspólna dla „Competitor visibility" i „Brand presence by topic".
 * Cienki adapter nad generycznym `MatrixHeatmapTable` (prezentacja tam): tu zostaje wyłącznie
 * raportowa wiedza o markach — etykiety, kolory, wyróżnienie wiersza marki badanej.
 */
export const ReportMatrixTable: React.FC<ReportMatrixTableProps> = ({
  columns,
  columnLabel,
  rows,
  brandColors,
  scrollable = false,
}) => {
  const { t } = useTranslation();

  const heatmapColumns: HeatmapColumn[] = columns.map((column) => ({
    key: column,
    header: columnLabel ? columnLabel(column) : column,
  }));

  const heatmapRows: HeatmapRow[] = rows.map((row, index) => {
    const label = brandLabel(row.brand);
    return {
      key: label,
      emphasized: row.isTracked,
      values: row.values,
      header: (
        <span className="report-matrix__brand-cell">
          <BrandIcon
            name={label}
            color={brandColorOf(brandColors, label, row.isTracked, index)}
            size="xs"
          />
          {label}
        </span>
      ),
    };
  });

  return (
    <MatrixHeatmapTable
      rowHeader={t('brand')}
      columns={heatmapColumns}
      rows={heatmapRows}
      rowHeaderNoWrap
      scrollable={scrollable}
    />
  );
};

export default ReportMatrixTable;
